/* eslint-disable max-lines */
/* eslint-disable @kluein/nectar/button-icon-size */
import tokens from '@kluein/design-tokens';
import { Tip } from '@kluein/klue-ui';
import { Menu, Button, Text, Box, Group, Divider } from '@mantine/core';
import { GearIcon, BookOpenIcon } from '@phosphor-icons/react';
import isNil from 'lodash/isNil';
import { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';

import { useAuth } from 'contexts/auth';
import { checkIsPromptLibraryEnabled } from 'contexts/auth/auth.utils';
import store from 'store';

import AdvancedPromptWizard from './AdvancedPromptWizard';
import { usePromptLibrary, type PromptType } from './hooks/usePromptLibrary';
import './PromptLibrary.css';
import RivalSelect from './PromptLibraryRival';

import type { CompanyType, RivalType } from 'api/api.types';
import type { RootState, Dispatch } from 'store/store.types';

type PromptLibraryProps = {
  onPromptSelect: (prompt: string) => void;
};

export function PromptLibrary({ onPromptSelect }: PromptLibraryProps) {
  const { t } = useTranslation(['Dashboard', 'Common']);
  const [opened, setOpened] = useState(false);
  const [activeTab, setActiveTab] = useState<string>('');
  const isSelectingRival = useRef(false);
  const dispatch = useDispatch<Dispatch>();
  const { isCurator, isAdmin, user, isCopyToBoardsEnabled } = useAuth();
  const [isAdvancedOpen, setIsAdvancedOpen] = useState(false);

  const company = useSelector<RootState, CompanyType | null>(
    (state) => state.auth.company,
  );

  const rivalIds = useSelector<RootState, number[] | undefined>((state) => {
    const { isLoading, allIds } = state.rivals;
    if (!isLoading) {
      return [...allIds].map((id) => +id);
    }
  }, shallowEqual);

  const rivalsById = useSelector(store.select.rivals.byId);

  // Get the stored rival ID from user data
  const storedRivalId = user?.userData?.promptLibrarySelectedRivalId;

  const allRivals = useMemo(() => {
    if (!rivalIds) return [];
    const rivals = rivalIds
      .map((id) => rivalsById.get(id.toString()))
      .filter(
        (rival): rival is RivalType =>
          Boolean(rival) && !rival?.isTemplate && !rival?.isDraft,
      );

    // Sort rivals alphabetically by name
    rivals.sort((a, b) => a.name.localeCompare(b.name));

    return rivals;
  }, [rivalIds, rivalsById]);

  // Compute the selected rival with validation and fallback logic
  const selectedRival = useMemo(() => {
    // If we have a stored rival ID, validate it
    if (!isNil(storedRivalId)) {
      const storedRival = rivalsById.get(storedRivalId.toString());

      // Check if stored rival exists, is not draft/template, and is in filtered list
      if (
        storedRival &&
        !storedRival.isDraft &&
        !storedRival.isTemplate &&
        allRivals.some((rival) => rival.id === storedRivalId)
      ) {
        return storedRival;
      }
    }

    // Fallback to first rival if stored rival is invalid or doesn't exist
    return allRivals.length > 0 ? allRivals[0] : null;
  }, [storedRivalId, rivalsById, allRivals]);

  const isEnabled = useMemo(() => {
    return checkIsPromptLibraryEnabled(company);
  }, [company]);

  // Fetch rival groups (which includes rivals) if they're not loaded
  useEffect(() => {
    if (!rivalIds && allRivals.length === 0) {
      dispatch.rivalGroups.fetchRivalGroupsFromApi(null);
    }
  }, [rivalIds, allRivals.length, dispatch.rivalGroups]);

  const prompts = usePromptLibrary({
    company,
    rival: selectedRival,
  });

  const handleRivalSelect = useCallback(
    (rival: RivalType) => {
      isSelectingRival.current = true;

      // Persist the rival selection to UserData
      dispatch.auth.updateUserPromptLibrarySelectedRival(rival.id);

      // Keep menu open after rival selection
      setTimeout(() => {
        setOpened(true);
        isSelectingRival.current = false;
      }, 0);
    },
    [dispatch.auth],
  );

  const getCurrentPrompt = useCallback(
    (prompt: PromptType) => {
      let currentPrompt = prompt.promptTemplate;

      if (company) {
        currentPrompt = currentPrompt.replace(/\$\{company\}/g, company.name);
      }
      if (selectedRival) {
        currentPrompt = currentPrompt.replace(
          /\$\{rival\}/g,
          selectedRival.name,
        );
      }

      return currentPrompt;
    },
    [company, selectedRival],
  );

  const renderPromptText = useCallback(
    (prompt: PromptType) => {
      const template = prompt.simpleTemplate;

      // Split by both company and rival tokens to process each part
      const parts = template.split(/(\$\{company\}|\$\{rival\})/);

      return parts.map((part, index) => {
        if (part === '${company}') {
          const companyName = company?.name || '[Company]';
          return (
            <span
              key={`company-${company?.id || 'no-company'}-${index}`}
              style={{ fontWeight: 'bold' }}
            >
              {companyName}
            </span>
          );
        } else if (part === '${rival}') {
          const rivalName = selectedRival?.name || '[Select rival above]';
          return (
            <span
              key={`rival-${selectedRival?.id || 'no-rival'}-${index}`}
              style={{ fontWeight: 'bold' }}
            >
              {rivalName}
            </span>
          );
        } else {
          return part;
        }
      });
    },
    [company, selectedRival],
  );

  // Group prompts by type
  const groupedPrompts = useMemo(() => {
    const groups: Record<string, PromptType[]> = {};

    // Filter prompts based on user role - non-admin, non-curator users only see 'seller' prompts
    const filteredPrompts =
      !isAdmin && !isCurator
        ? prompts.filter((prompt) => prompt.type === 'seller')
        : prompts;

    filteredPrompts.forEach((prompt) => {
      if (!groups[prompt.type]) {
        groups[prompt.type] = [];
      }
      groups[prompt.type].push(prompt);
    });

    return groups;
  }, [prompts, isAdmin, isCurator]);

  // Define the order for prompt types
  const typeOrder = ['battlecards', 'seller', 'pmm', 'executive', 'simulation'];

  // Get available types with prompts
  const availableTypes = typeOrder.filter(
    (type) => groupedPrompts[type] && groupedPrompts[type].length > 0,
  );

  // Initialize active tab to first available type
  useEffect(() => {
    if (availableTypes.length > 0 && !activeTab) {
      setActiveTab(availableTypes[0]);
    }
  }, [availableTypes, activeTab]);

  // Fallback - use first available type if no activeTab is set
  const currentActiveTab =
    activeTab || (availableTypes.length > 0 ? availableTypes[0] : '');

  if (!isEnabled) {
    return null;
  }

  // Don't render if rivals are still loading
  if (!rivalIds && allRivals.length === 0) {
    return null;
  }

  return (
    <div className="prompt-library-hide-small">
      <Menu
        shadow="md"
        width={400}
        position="bottom-start"
        withinPortal={true}
        offset={8}
        middlewares={{
          flip: true,
          shift: { padding: 8 },
          size: { padding: 8 },
        }}
        opened={opened}
        onChange={(newOpened) => {
          if (!isSelectingRival.current) {
            setOpened(newOpened);
          }
        }}
        closeOnItemClick={false}
      >
        <Tip
          content={t('Dashboard:askKlue.promptLibrary.tooltip')}
          color="dark"
          variant="small"
        >
          <Menu.Target>
            <Button
              size="xs"
              data-test-id="prompt-library-button"
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
              variant={opened ? 'highlight' : 'light'}
            >
              <BookOpenIcon size={18} />
            </Button>
          </Menu.Target>
        </Tip>

        <Menu.Dropdown
          style={{
            height: 'min(50vh, 500px)',
            display: 'flex',
            flexDirection: 'column',
          }}
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <Box
            p="xs"
            style={{
              display: 'flex',
              flexDirection: 'column',
              flex: 1,
              overflow: 'hidden',
            }}
          >
            {/* Global rival selector at the top */}
            {allRivals.length > 0 && (
              <Box mb="sm" pb="sm">
                <Text size="xs" mb="xs" style={{ color: '#666' }}>
                  {t(
                    'Dashboard:askKlue.promptLibrary.selectRivalForAllPrompts',
                  )}
                  :
                </Text>
                <RivalSelect
                  rivals={allRivals}
                  selectedRival={selectedRival}
                  onRivalSelect={handleRivalSelect}
                />
              </Box>
            )}
            <Box
              style={{
                display: 'flex',
                flexDirection: 'column',
                flex: 1,
                overflow: 'hidden',
              }}
            >
              {/* Render tabs only if multiple types exist */}
              {availableTypes.length > 1 && (
                <Box mb="md" pb="sm">
                  <Group gap="lg" justify="center">
                    {availableTypes.map((type) => (
                      <Box
                        key={type}
                        onClick={(e) => {
                          e.stopPropagation();
                          setActiveTab(type);
                        }}
                        onMouseDown={(e) => e.stopPropagation()}
                        style={{
                          cursor: 'pointer',
                          position: 'relative',
                        }}
                      >
                        <Text
                          size="sm"
                          fw={currentActiveTab === type ? '600' : '400'}
                          c={
                            currentActiveTab === type
                              ? tokens.color.neutral[900]
                              : tokens.color.neutral[800]
                          }
                        >
                          {t(`Dashboard:askKlue.promptLibrary.types.${type}`)}
                        </Text>
                        {/* Active tab indicator */}
                        {currentActiveTab === type && (
                          <Box
                            style={{
                              position: 'absolute',
                              bottom: '-7px',
                              left: 0,
                              right: 0,
                              height: '3px',
                              backgroundColor: tokens.color.highlight[1000],
                              borderRadius: '2px',
                            }}
                          />
                        )}
                      </Box>
                    ))}
                  </Group>
                </Box>
              )}

              {/* Render prompts for active tab */}
              {currentActiveTab && groupedPrompts[currentActiveTab] && (
                <Box
                  style={{
                    flex: 1,
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    width: '100%',
                  }}
                >
                  {groupedPrompts[currentActiveTab].map((prompt) => (
                    <Menu.Item
                      key={prompt.id}
                      onClick={(e) => {
                        e.stopPropagation();
                        const currentPrompt = getCurrentPrompt(prompt);
                        onPromptSelect(currentPrompt);
                        setOpened(false);
                      }}
                      onMouseDown={(e) => e.stopPropagation()}
                      styles={{
                        item: {
                          padding: '12px 16px',
                        },
                      }}
                    >
                      <Box>
                        <Text size="sm" c={tokens.color.primitives.blue[450]}>
                          {renderPromptText(prompt)}
                        </Text>
                      </Box>
                    </Menu.Item>
                  ))}
                </Box>
              )}
            </Box>
            {isCopyToBoardsEnabled && isCurator && (
              <>
                <Divider my="xs" />
                <Menu.Item
                  leftSection={<GearIcon size={16} />}
                  onClick={(e) => {
                    e.stopPropagation();
                    setOpened(false);
                    setIsAdvancedOpen(true);
                  }}
                  onMouseDown={(e) => e.stopPropagation()}
                >
                  <Text size="sm" fw={500}>
                    {t('Dashboard:askKlue.promptLibrary.advanced')}
                  </Text>
                </Menu.Item>
              </>
            )}
          </Box>
        </Menu.Dropdown>
      </Menu>
      {isAdvancedOpen && (
        <AdvancedPromptWizard onClose={() => setIsAdvancedOpen(false)} />
      )}
    </div>
  );
}

export default PromptLibrary;
